import { DialogElement } from "theme";
import { FocusTrap, Delegate, animate } from "vendor";

const COOKIE_NAME = "age_accepted";
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;
// Cookie Information banner (injected by uc.js outside of <age-popup>) must stay clickable while the age popup is open
const COOKIE_BANNER_SELECTOR = "#cookie-information-template-wrapper";
const CRAWLER_REGEX = /bot|crawl|spider|slurp|facebookexternalhit|bingpreview|lighthouse/i;

export class AgePopup extends DialogElement {
  #delegate = new Delegate(this);
  #focusTrap;

  connectedCallback() {
    super.connectedCallback();

    this.setAttribute("aria-modal", "true");

    this.#delegate.off()
      .on("click", '[data-action="accept"]', this.#accept.bind(this))
      .on("click", '[data-action="decline"]', this.#decline.bind(this));

    if (this.#shouldAppear) {
      this.show(false);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#delegate.off();
  }

  get redirectUrl() {
    return this.getAttribute("redirect-url") || "/404";
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }

  get clickOutsideDeactivates() {
    return false;
  }

  get initialFocus() {
    return this.querySelector('[data-action="accept"]');
  }

  get focusTrap() {
    this.#focusTrap = this.#focusTrap || new FocusTrap.createFocusTrap(this, {
      escapeDeactivates: false,
      clickOutsideDeactivates: false,
      allowOutsideClick: (event) => event.target instanceof Element && event.target.closest(COOKIE_BANNER_SELECTOR) !== null,
      initialFocus: this.initialFocus,
      fallbackFocus: this,
      tabbableOptions: { getShadowRoot: true },
      preventScroll: true
    });

    return this.#focusTrap;
  }

  createEnterAnimationControls() {
    return animate(this, { opacity: [0, 1] }, { duration: 0.3 });
  }

  createLeaveAnimationControls() {
    return animate(this, { opacity: [1, 0] }, { duration: 0.3 });
  }

  get #shouldAppear() {
    const hasAcceptedAge = document.cookie.split("; ").some((cookie) => cookie.startsWith(`${COOKIE_NAME}=`));
    return !hasAcceptedAge && !CRAWLER_REGEX.test(navigator.userAgent);
  }

  #accept() {
    document.cookie = `${COOKIE_NAME}=true; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
    this.hide();
  }

  #decline() {
    window.location.href = this.redirectUrl;
  }
}

if (!window.customElements.get("age-popup")) {
  window.customElements.define("age-popup", AgePopup);
}
